import {
  Angle,
  BezierCurve,
  Ellipse,
  LineSegment,
  Path,
  Point,
  Rect,
  bezier,
  lineSegment,
  path,
  point,
  radians
} from '@geom';
import type { EID, NID } from '@graph';
import {
  BezierSeg as AGLBezierSeg,
  Curve as AGLCurve,
  CurveFactory as AGLCurveFactory,
  Edge as AGLEdge,
  Ellipse as AGLEllipse,
  GeomEdge as AGLGeomEdge,
  GeomGraph as AGLGeomGraph,
  GeomNode as AGLGeomNode,
  Graph as AGLGraph,
  LineSegment as AGLLineSegment,
  Node as AGLNode,
  Point as AGLPoint,
  RRect as AGLRRect,
  Size as AGLSize,
  SplineRouter as AGLSplineRouter,
  BundlingSettings,
  EdgeRoutingSettings, // Import RRect directly
  type ICurve as AGLICurve
} from 'msagl';
import { expectDefined } from '@utils';

export class EdgeRouter {
  private graph: AGLGeomGraph;
  private compoundNodes: Map<NID, AGLGeomGraph>;
  private nodes: Map<NID, AGLGeomNode>;
  private edges: Map<EID, AGLGeomEdge>;

  constructor() {
    this.graph = new AGLGeomGraph(new AGLGraph('graph'));
    this.graph.labelSize = new AGLSize(0, 0);
    this.compoundNodes = new Map();
    this.nodes = new Map();
    this.edges = new Map();
  }

  addCompoundNode(node: NID, bounds: Rect, cornerRadius: number, parent?: NID) {
    const compound = new AGLGeomGraph(new AGLGraph(node));
    setCompoundBounds(compound, bounds, cornerRadius);
    this.compoundNodes.set(node, compound);
    const parentCompound = parent
      ? expectDefined(this.compoundNodes.get(parent))
      : this.graph;
    parentCompound.addNode(compound);
  }

  addLeafNode(node: NID, bounds: Rect, cornerRadius: number, parent?: NID) {
    const geomNode = new AGLGeomNode(new AGLNode(node));
    setNodeBounds(geomNode, bounds, cornerRadius);
    this.nodes.set(node, geomNode);
    const parentCompound = parent
      ? expectDefined(this.compoundNodes.get(parent))
      : this.graph;
    parentCompound.addNode(geomNode);
  }

  addEdge(edge: EID, source: NID, target: NID) {
    const sourceNode = this.nodes.get(source);
    if (!sourceNode) {
      throw new Error(`Layouter.addEdge: Source node ${source} not found`);
    }
    const targetNode = this.nodes.get(target);
    if (!targetNode) {
      throw new Error(`Layouter.addEdge: Target node ${target} not found`);
    }
    const geomEdge = makeEdge(sourceNode, targetNode);
    this.edges.set(edge, geomEdge);
  }

  routeEdges(bundledRouting: boolean): Map<EID, Path> {
    const paths = new Map<EID, Path>();

    // EdgeRoutingSettings Defaults:
    //    KeepOriginalSpline: false
    //    ConeAngle: 0.5235987755982988 (Math.PI / 6 == 30 degrees)
    //    EdgeRoutingMode: 0 (Spline)
    //    IncrementalRoutingThreshold: 5000000
    //    Padding: 2
    //    PolylinePadding: 10
    //    RouteMultiEdgesAsBundles: true
    //    RoutingToParentConeAngle: 0.5235987755982988 (Math.PI / 6 == 30 degrees)
    //    SimpleSelfLoopsForParentEdgesThreshold: 200

    // BundlingSettings Defaults:
    //    capacityOverflowCoefficient: 1000
    //    RotateBundles: false
    //    MaxHubRadius: 50
    //    MinHubRadius: 0.1
    //    CreateUnderlyingPolyline: false
    //    pathLengthImportance: 500
    //    inkImportance: 0.01
    //    edgeSeparation: 0.5
    //    edgeWidthShrinkCoeff: 1
    //    useCubicBezierSegmentsInsideOfHubs: false
    //    angleThreshold: 0.7853981633974483 (Math.PI / 4 == 45 degrees)
    //    hubRepulsionImportance: 100
    //    bundleRepulsionImportance: 100
    //    minimalRatioOfGoodCdtEdges: 0.9
    //    highestQuality: true
    //    KeepOverlaps: false
    //    StopAfterShortestPaths: false

    let splineRouter: AGLSplineRouter;

    if (bundledRouting) {
      const bundlingSettings = new BundlingSettings();
      bundlingSettings.edgeSeparation = 10;
      splineRouter = AGLSplineRouter.mk5(this.graph, 2, 4, Math.PI / 6, bundlingSettings);
    } else {
      const settings = new EdgeRoutingSettings();
      splineRouter = AGLSplineRouter.mk2(this.graph, settings);
    }

    // console.log("🎁 🟢 splineRouter.run Start");
    splineRouter.run();
    // console.log("🎁 🔴 splineRouter.run End");

    this.edges.forEach((geomEdge, edge) => {
      const iCurve = geomEdge.curve as AGLICurve | undefined; // Router may have failed silently
      if (!iCurve) {
        console.warn(`EdgeRouter.routeSplineEdges: Edge ${edge} has no curve`);
        return;
      }
      const path = aglICurveToPath(geomEdge.curve);
      paths.set(edge, path);
    });
    return paths;
  }
}

function toAGLPoint(p: Point): AGLPoint {
  return new AGLPoint(p.x, p.y);
}

function fromAGLPoint(p: AGLPoint): Point {
  return point(p.x, p.y);
}

function fromAGLAngle(angle: number): Angle {
  return radians(angle);
}

function aglBezierToBezierCurve(bezierSeg: AGLBezierSeg): BezierCurve {
  return bezier([
    fromAGLPoint(bezierSeg.b[0]),
    fromAGLPoint(bezierSeg.b[1]),
    fromAGLPoint(bezierSeg.b[2]),
    fromAGLPoint(bezierSeg.b[3])
  ]);
}

function aglLineToLineSegment(line: AGLLineSegment): LineSegment {
  return lineSegment(fromAGLPoint(line.start), fromAGLPoint(line.end));
}

function aglEllipseToEllipse(ellipse: AGLEllipse): Ellipse {
  const centerEllipse = {
    center: fromAGLPoint(ellipse.center),
    aAxis: fromAGLPoint(ellipse.aAxis),
    bAxis: fromAGLPoint(ellipse.bAxis),
    startAngle: fromAGLAngle(ellipse.parStart),
    endAngle: fromAGLAngle(ellipse.parEnd)
  };
  return Ellipse.fromCenterEllipse(centerEllipse);
}

function aglCurveToPath(curve: AGLCurve): Path {
  const pth = path();
  curve.segs.forEach(seg => {
    if (seg instanceof AGLBezierSeg) {
      pth.addSegment(aglBezierToBezierCurve(seg));
    } else if (seg instanceof AGLLineSegment) {
      pth.addSegment(aglLineToLineSegment(seg));
    } else if (seg instanceof AGLEllipse) {
      pth.addSegment(aglEllipseToEllipse(seg));
    }
  });
  return pth.build();
}

function aglICurveToPath(curve: AGLICurve): Path {
  if (curve instanceof AGLCurve) {
    return aglCurveToPath(curve);
  } else if (curve instanceof AGLBezierSeg) {
    return Path.fromSegments([aglBezierToBezierCurve(curve)]);
  } else if (curve instanceof AGLLineSegment) {
    return Path.fromSegments([aglLineToLineSegment(curve)]);
  } else if (curve instanceof AGLEllipse) {
    return Path.fromSegments([aglEllipseToEllipse(curve)]);
  }
  console.error(curve);
  throw new Error('Unsupported curve type');
}

function setNodeBounds(node: AGLGeomNode, bounds: Rect, cornerRadius: number) {
  node.boundaryCurve = AGLCurveFactory.mkRectangleWithRoundedCorners(
    bounds.width,
    bounds.height,
    cornerRadius,
    cornerRadius,
    toAGLPoint(bounds.center)
  );
}

function setCompoundBounds(graph: AGLGeomGraph, bounds: Rect, cornerRadius: number) {
  // Uses our forked version of MSAGL that publicly exposes `RRect` and the
  // `rrect` property of GeomGraph
  graph.rrect = new AGLRRect({
    left: bounds.left,
    top: bounds.top,
    right: bounds.right,
    bottom: bounds.bottom,
    radX: cornerRadius,
    radY: cornerRadius
  });
}

function makeEdge(source: AGLGeomNode, target: AGLGeomNode): AGLGeomEdge {
  return new AGLGeomEdge(new AGLEdge(source.node, target.node));
}
